import LocalizedLink from "@/components/LocalizedLink";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LogOut, Settings, Package, Shield, LifeBuoy } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useMobileMenu } from "@/contexts/MobileMenuContext";
import { useTranslation } from "react-i18next";
import logoFull from "@/assets/logo-full.webp";
import xosLogo from "@/assets/xos-logo.webp";
import xpandLabsLogo from "@/assets/xpand-labs-logo.png";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  XpandMic,
  XpandCalendar,
  XpandUsers,
  XpandStore,
  XpandGift,
  XpandZap,
  XpandPlug,
  XpandBuilding,
  XpandHeadphones,
  XpandTrending,
  XpandMegaphone,
  XpandCode,
  XpandSparkles,
  XpandBook,
  XpandFileText,
  XpandGlobe,
  XpandArrowRight,
  XpandChevronDown,
  XpandMenu,
  XpandX,
  XpandUtensils } from "@/components/icons/XpandIcons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export function Navbar() {
  const { t } = useTranslation();
  const { mobileMenuOpen, setMobileMenuOpen } = useMobileMenu();
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [expandedMobileMenu, setExpandedMobileMenu] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [sellerLogo, setSellerLogo] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, role, isSeller, isAdmin, isSupport } = useAuth();

  // Pages with light/white backgrounds need dark nav text
  const lightPages = ["/white-label"];
  const normalizedPath = location.pathname.replace(/^\/(en|es)(?=\/|$)/, "") || "/";
  const isLightPage = lightPages.includes(normalizedPath);

  const aiStoreMenuItems = [
    {
      category: t("nav.browseByType"),
      items: [
        { name: t("nav.doneForYou"), description: t("nav.doneForYouDesc"), icon: XpandGift, href: "/store?type=done-for-you" },
        { name: t("nav.easyInstall"), description: t("nav.easyInstallDesc"), icon: XpandZap, href: "/store?type=easy-install" },
        { name: t("nav.technical"), description: t("nav.technicalDesc"), icon: XpandCode, href: "/store?type=api-integration" },
      ]
    },
    {
      category: t("nav.topCategories"),
      items: [
        { name: t("nav.customerSupport"), description: t("nav.aiPoweredSupport"), icon: XpandHeadphones, href: "/store?category=Support" },
        { name: t("nav.sales"), description: t("nav.closeMoreDeals"), icon: XpandTrending, href: "/store?category=Sales" },
        { name: t("nav.marketing"), description: t("nav.growYourAudience"), icon: XpandMegaphone, href: "/store?category=Marketing" },
      ]
    }
  ];

  const productMenuItems = [
    {
      category: t("nav.platform"),
      items: [
        { name: t("nav.voiceAI"), description: t("nav.voiceAIDesc"), icon: XpandMic, href: "/platform/voice-ai" },
        { name: t("nav.smartCRM"), description: t("nav.smartCRMDesc"), icon: XpandUsers, href: "/platform/smart-crm" },
        { name: t("nav.smartBooking"), description: t("nav.smartBookingDesc"), icon: XpandCalendar, href: "/platform/smart-booking" },
      ]
    },
    {
      category: t("nav.technology"),
      items: [
        { name: "XPAND OS", description: t("nav.memoryOS"), icon: XpandZap, href: "/platform/xpand-os" },
      ]
    }
  ];

  const solutionsMenuItems = [
    // Left column - first 5 items
    { name: t("nav.hospitality"), description: t("nav.hospitalityDesc"), icon: XpandUtensils, href: "/solutions/hospitality" },
    { name: t("nav.healthcare"), description: t("nav.healthcareDesc"), icon: XpandHeadphones, href: "/solutions/healthcare" },
    { name: t("nav.realEstate"), description: t("nav.realEstateDesc"), icon: XpandTrending, href: "/solutions/real-estate" },
    { name: t("nav.travelTourism"), description: t("nav.travelTourismDesc"), icon: XpandGlobe, href: "/solutions/travel-tourism" },
    { name: t("nav.beautyWellness"), description: t("nav.beautyWellnessDesc"), icon: XpandSparkles, href: "/solutions/beauty-salons" },
    // Right column - next 5 items
    { name: t("nav.hotels"), description: t("nav.hotelsDesc"), icon: XpandBuilding, href: "/solutions/hotels" },
    { name: t("nav.technology"), description: t("nav.technologyDesc"), icon: XpandCode, href: "/solutions/technology" },
    { name: t("nav.legalFinance"), description: t("nav.legalFinanceDesc"), icon: XpandFileText, href: "/solutions/legal-finance" },
    { name: t("nav.marketing"), description: t("nav.marketingDesc"), icon: XpandMegaphone, href: "/solutions/marketing" },
    { name: t("nav.sales"), description: t("nav.salesDesc"), icon: XpandTrending, href: "/solutions/sales" },
  ];

  const resourcesMenuItems = [
    { name: t("nav.liveDemos"), description: t("nav.liveDemosDesc"), icon: XpandMic, href: "/live-demo" },
    { name: t("nav.schedule"), description: t("nav.scheduleDesc"), icon: XpandCalendar, href: "/schedule" },
    { name: t("nav.enterpriseGovernment"), description: t("nav.enterpriseGovernmentDesc"), icon: XpandBuilding, href: "/enterprise" },
    { name: t("nav.whiteLabel"), description: t("nav.whiteLabelDesc"), icon: XpandGlobe, href: "/white-label" },
    { name: t("nav.affiliates"), description: t("nav.affiliatesDesc"), icon: XpandUsers, href: "/affiliates" },
    { name: t("nav.documentation"), description: t("nav.documentationDesc"), icon: XpandBook, href: "/docs" },
    { name: t("nav.blogs"), description: t("nav.blogsDesc"), icon: XpandFileText, href: "/blog" },
    { name: t("nav.xpandLabs"), description: t("nav.xpandLabsDesc"), href: "/partners", logo: xpandLabsLogo },
  ];

  const navigation = [
    { name: t("nav.aiStore"), href: "/store", hasDropdown: true, menuType: "store" },
    { name: t("nav.product"), href: "#", hasDropdown: true, menuType: "product" },
    { name: t("nav.solutions"), href: "#", hasDropdown: true, menuType: "solutions" },
    { name: t("nav.pricing"), href: "/pricing", hasDropdown: false },
    { name: t("nav.resources"), href: "#", hasDropdown: true, menuType: "resources" },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch seller logo if user is a seller
  useEffect(() => {
    const fetchSellerLogo = async () => {
      if (!user || !isSeller) return;
      const { data } = await supabase
        .from("seller_profiles")
        .select("logo_url")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data?.logo_url) setSellerLogo(data.logo_url);
    };
    fetchSellerLogo();
  }, [user, isSeller]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setExpandedMobileMenu(null);
  }, [location.pathname]);

  const toggleMobileSubmenu = (menuName: string) => {
    setExpandedMobileMenu(expandedMobileMenu === menuName ? null : menuName);
  };

  const getMobileMenuItems = (menuType: string) => {
    switch (menuType) {
      case "store":
        return aiStoreMenuItems;
      case "product":
        return productMenuItems;
      case "solutions":
        return [{ category: t("nav.byIndustry"), items: solutionsMenuItems }];
      case "resources":
        return [{ category: t("nav.resources"), items: resourcesMenuItems }];
      default:
        return [];
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const getUserInitials = () => {
    if (!user?.email) return "U";
    return user.email.charAt(0).toUpperCase();
  };

  const renderMegaMenu = (menuType: string) => {
    if (menuType === "store") {
      return (
        <div className="absolute top-full left-1/2 -translate-x-1/2 w-[650px] p-6 bg-[#0a0a0f] backdrop-blur-3xl border border-white/10 rounded-2xl shadow-2xl shadow-black z-50">
          <div className="grid grid-cols-2 gap-8">
            {aiStoreMenuItems.map((category) => (
              <div key={category.category}>
                <p className="text-[10px] font-semibold tracking-widest text-primary uppercase mb-4">{category.category}</p>
                <div className="space-y-1">
                  {category.items.map((item) => (
                    <LocalizedLink 
                      key={item.name} 
                      to={item.href} 
                      className="flex items-center gap-4 p-3 -mx-3 rounded-xl hover:bg-white/5 transition-all duration-200 group"
                      onClick={() => setActiveMenu(null)}
                    >
                      <div className="w-10 h-10 rounded-xl bg-white/5 group-hover:bg-primary/15 flex items-center justify-center transition-colors border border-white/10">
                        <item.icon className="w-5 h-5 text-primary group-hover:text-primary transition-colors" strokeWidth={1.5} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-normal text-white group-hover:text-white transition-colors">{item.name}</p>
                        <p className="text-xs text-neutral-400 font-light mt-0.5">{item.description}</p>
                      </div>
                      <XpandArrowRight className="w-4 h-4 text-white/30 group-hover:text-primary group-hover:translate-x-1 transition-all opacity-0 group-hover:opacity-100" />
                    </LocalizedLink>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-6 border-t border-white/10">
            <LocalizedLink 
              to="/store" 
              className="flex items-center justify-between p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all group border border-white/10"
              onClick={() => setActiveMenu(null)}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center border border-primary/30">
                  <XpandStore className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-medium text-white">{t("nav.browseAllAgents")}</p>
                  <p className="text-xs text-neutral-400">{t("nav.agentsAvailable")}</p>
                </div>
              </div>
              <XpandArrowRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
            </LocalizedLink>
          </div>
        </div>
      );
    }

    if (menuType === "product") {
      return (
        <div className="absolute top-full left-1/2 -translate-x-1/2 w-[750px] p-6 bg-[#0a0a0f] backdrop-blur-3xl border border-white/10 rounded-2xl shadow-2xl shadow-black z-50">
          <div className="grid grid-cols-[1fr,1fr] gap-6">
            {/* Left side - Platform items */}
            <div>
              <p className="text-[10px] font-semibold tracking-widest text-primary uppercase mb-4">{t("nav.platform")}</p>
              <div className="space-y-1">
                {productMenuItems[0].items.map((item) => (
                  <LocalizedLink 
                    key={item.name} 
                    to={item.href} 
                    className="flex items-center gap-4 p-3 -mx-3 rounded-xl hover:bg-white/5 transition-all duration-200 group"
                    onClick={() => setActiveMenu(null)}
                  >
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center transition-colors border relative overflow-hidden",
                      item.href === "/platform/voice-ai" 
                        ? "bg-gradient-to-br from-primary/30 to-purple-500/20 border-primary/40"
                        : "bg-white/5 group-hover:bg-primary/15 border-white/10"
                    )}>
                      <item.icon className="w-5 h-5 text-primary relative z-10" strokeWidth={1.5} />
                      {item.href === "/platform/voice-ai" && (
                        <div 
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12"
                          style={{
                            animation: "shimmer 2s ease-in-out infinite" }}
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className={cn(
                          "text-sm font-normal transition-colors",
                          item.href === "/platform/voice-ai" 
                            ? "bg-gradient-to-r from-primary to-purple-400 bg-clip-text text-transparent font-medium"
                            : "text-white group-hover:text-white"
                        )}>{item.name}</p>
                        {(item as any).badge && (
                          <span className="px-2 py-0.5 text-[10px] font-medium bg-primary/20 text-primary rounded-full">
                            {(item as any).badge}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-neutral-400 font-light mt-0.5">{item.description}</p>
                    </div>
                    <XpandArrowRight className="w-4 h-4 text-white/30 group-hover:text-primary group-hover:translate-x-1 transition-all opacity-0 group-hover:opacity-100" />
                  </LocalizedLink>
                ))}
              </div>
              <style>{`
                @keyframes shimmer {
                  0% { transform: translateX(-200%) skewX(-12deg); }
                  100% { transform: translateX(200%) skewX(-12deg); }
                }
              `}</style>
            </div>
            
            {/* Right side - XPAND OS Memory Card */}
            <div className="flex flex-col">
              <p className="text-[10px] font-semibold tracking-widest text-primary/60 uppercase mb-3">XPAND OS</p>
              <LocalizedLink 
                to="/platform/xpand-os" 
                className="flex-1 flex flex-col rounded-2xl overflow-hidden border border-white/[0.08] hover:border-primary/40 transition-all duration-500 group relative bg-[#0a0a1a]"
                onClick={() => setActiveMenu(null)}
              >
                {/* Animated background mesh */}
                <div className="absolute inset-0 opacity-40 group-hover:opacity-60 transition-opacity duration-700" style={{
                  background: `
                    radial-gradient(ellipse at 30% 20%, hsl(270 70% 50% / 0.25) 0%, transparent 50%),
                    radial-gradient(ellipse at 70% 80%, hsl(250 60% 40% / 0.15) 0%, transparent 50%),
                    radial-gradient(ellipse at 50% 50%, hsl(280 50% 30% / 0.1) 0%, transparent 60%)
                  `
                }} />
                
                {/* Scanning line effect */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent animate-pulse" style={{ top: '30%' }} />
                  <div className="absolute w-full h-px bg-gradient-to-r from-transparent via-primary/15 to-transparent animate-pulse [animation-delay:1s]" style={{ top: '60%' }} />
                </div>

                {/* Visual area */}
                <div className="flex-1 p-6 flex items-center justify-center relative">
                  {/* Orbital rings */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-[100px] h-[100px] rounded-full border border-primary/[0.08]" style={{ animation: 'spin 20s linear infinite' }} />
                    <div className="absolute w-[140px] h-[140px] rounded-full border border-primary/[0.05] border-dashed" style={{ animation: 'spin 30s linear infinite reverse' }} />
                    <div className="absolute w-[170px] h-[170px] rounded-full border border-primary/[0.03]" style={{ animation: 'spin 40s linear infinite' }} />
                  </div>
                  
                  {/* Floating data nodes */}
                  <div className="absolute top-5 right-6 w-1.5 h-1.5 rounded-full bg-primary/50 animate-pulse" />
                  <div className="absolute bottom-8 left-5 w-1 h-1 rounded-full bg-violet-400/40 animate-pulse [animation-delay:0.7s]" />
                  <div className="absolute top-10 left-8 w-1 h-1 rounded-full bg-blue-400/30 animate-pulse [animation-delay:1.4s]" />
                  
                  {/* Core logo */}
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-full bg-primary/10 blur-xl group-hover:bg-primary/20 transition-all duration-700" />
                    <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 via-primary/10 to-transparent flex items-center justify-center border border-primary/20 group-hover:border-primary/40 group-hover:shadow-[0_0_30px_hsl(270_70%_50%_/_0.2)] transition-all duration-500">
                      <img src={xosLogo} alt="XPAND OS" width={40} height={40} className="w-10 h-10 object-contain group-hover:scale-110 transition-transform duration-500" />
                    </div>
                  </div>
                </div>
                
                {/* Bottom content with glass effect */}
                <div className="relative p-4 border-t border-white/[0.05] bg-white/[0.02] backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[13px] font-medium text-white group-hover:text-primary/90 transition-colors">{t("nav.xpandOSMemory")}</p>
                      <p className="text-[11px] text-white/30 font-light mt-0.5">{t("nav.xpandOSMemoryDesc")}</p>
                    </div>
                    <div className="w-7 h-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center group-hover:bg-primary/20 transition-all">
                      <XpandArrowRight className="w-3.5 h-3.5 text-primary group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                  {/* Micro status indicator */}
                  <div className="flex items-center gap-1.5 mt-2.5">
                    <div className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-[9px] text-white/25 font-light tracking-wider uppercase">Memory Active</span>
                  </div>
                </div>
              </LocalizedLink>
            </div>
          </div>
          
          {/* Featured Banner */}
          <div className="mt-6 pt-6 border-t border-white/10">
            <LocalizedLink 
              to="/platform/voice-ai" 
              className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-primary/10 via-purple-500/5 to-transparent border border-primary/20 hover:border-primary/40 transition-all group"
              onClick={() => setActiveMenu(null)}
            >
              <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center border border-primary/30">
                <XpandMic className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-white">{t("nav.tryVoiceAI")}</p>
                <p className="text-xs text-neutral-400">{t("nav.voiceAIStats")}</p>
              </div>
              <XpandArrowRight className="w-5 h-5 text-primary group-hover:translate-x-1 transition-transform" />
            </LocalizedLink>
          </div>
        </div>
      );
    }

    if (menuType === "solutions") {
      const leftItems = solutionsMenuItems.slice(0, 5);
      const rightItems = solutionsMenuItems.slice(5, 10);
      
      return (
        <div className="absolute top-full left-1/2 -translate-x-1/2 w-[700px] p-6 bg-[#0a0a0f] backdrop-blur-3xl border border-white/10 rounded-2xl shadow-2xl shadow-black z-50">
          <p className="text-[10px] font-semibold tracking-widest text-primary uppercase mb-4">{t("nav.byIndustry")}</p>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1">
              {leftItems.map((item) => (
                <LocalizedLink 
                  key={item.name} 
                  to={item.href} 
                  className="flex items-center gap-4 p-3 -mx-3 rounded-xl hover:bg-white/5 transition-all duration-200 group"
                  onClick={() => setActiveMenu(null)}
                >
                  <div className="w-10 h-10 rounded-xl bg-white/5 group-hover:bg-primary/15 flex items-center justify-center transition-colors border border-white/10">
                    <item.icon className="w-5 h-5 text-primary group-hover:text-primary transition-colors" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-normal text-white group-hover:text-white transition-colors">{item.name}</p>
                    <p className="text-xs text-neutral-400 font-light mt-0.5">{item.description}</p>
                  </div>
                  <XpandArrowRight className="w-4 h-4 text-white/30 group-hover:text-primary group-hover:translate-x-1 transition-all opacity-0 group-hover:opacity-100" />
                </LocalizedLink>
              ))}
            </div>
            <div className="space-y-1">
              {rightItems.map((item) => (
                <LocalizedLink 
                  key={item.name} 
                  to={item.href} 
                  className="flex items-center gap-4 p-3 -mx-3 rounded-xl hover:bg-white/5 transition-all duration-200 group"
                  onClick={() => setActiveMenu(null)}
                >
                  <div className="w-10 h-10 rounded-xl bg-white/5 group-hover:bg-primary/15 flex items-center justify-center transition-colors border border-white/10">
                    <item.icon className="w-5 h-5 text-primary group-hover:text-primary transition-colors" strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-normal text-white group-hover:text-white transition-colors">{item.name}</p>
                    <p className="text-xs text-neutral-400 font-light mt-0.5">{item.description}</p>
                  </div>
                  <XpandArrowRight className="w-4 h-4 text-white/30 group-hover:text-primary group-hover:translate-x-1 transition-all opacity-0 group-hover:opacity-100" />
                </LocalizedLink>
              ))}
            </div>
          </div>
        </div>
      );
    }

    if (menuType === "resources") {
      const resourcesMenuClasses = isLightPage
        ? {
            shell: "border-border/80 bg-background/95 shadow-[0_32px_90px_-36px_rgba(0,0,0,0.18)]",
            overlay: "bg-background/92 backdrop-blur-2xl",
            overlayTint: "bg-background/35",
            itemHover: "hover:bg-muted/70",
            iconWrap: "border-border/80 bg-secondary/55 group-hover:border-border group-hover:bg-secondary/85",
            title: "text-foreground/92 group-hover:text-foreground",
            description: "text-muted-foreground",
            arrow: "text-foreground/30 group-hover:text-primary",
            label: "text-primary/80",
          }
        : {
            shell: "border-white/[0.08] shadow-[0_32px_90px_-36px_rgba(0,0,0,0.98)]",
            overlay: "bg-[linear-gradient(180deg,hsl(240_18%_7%_/0.98),hsl(240_20%_5%_/0.995))] backdrop-blur-3xl",
            overlayTint: "bg-black/30",
            itemHover: "hover:bg-white/[0.03]",
            iconWrap: "border-white/[0.06] bg-white/[0.02] group-hover:border-white/[0.1] group-hover:bg-white/[0.04]",
            title: "text-white/92 group-hover:text-white",
            description: "text-white/42",
            arrow: "text-white/24 group-hover:text-primary",
            label: "text-primary/80",
          };

      return (
        <div className={cn("absolute top-full left-1/2 z-50 w-[360px] -translate-x-1/2 overflow-hidden rounded-[20px] border", resourcesMenuClasses.shell)}>
          <div className={cn("absolute inset-0", resourcesMenuClasses.overlay)} />
          <div className={cn("absolute inset-0", resourcesMenuClasses.overlayTint)} />
          <div className="relative p-3">
          <p className={cn("px-2 pb-2 text-[10px] font-semibold tracking-widest uppercase", resourcesMenuClasses.label)}>{t("nav.resources")}</p>
          <div className="space-y-0.5">
            {resourcesMenuItems.map((item) => {
              const content = (
                <>
                  <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg border transition-colors overflow-hidden p-0", resourcesMenuClasses.iconWrap)}>
                    {item.logo ? (
                      <img src={item.logo} alt={item.name} className="h-full w-full object-cover opacity-95 transition-opacity group-hover:opacity-100" />
                    ) : item.icon ? (
                      <item.icon className="w-5 h-5 text-primary group-hover:text-primary transition-colors" strokeWidth={1.5} />
                    ) : null}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={cn("text-sm font-normal transition-colors", resourcesMenuClasses.title)}>{item.name}</p>
                    <p className={cn("mt-0.5 text-xs font-light", resourcesMenuClasses.description)}>{item.description}</p>
                  </div>
                  <XpandArrowRight className={cn("w-4 h-4 shrink-0 transition-all group-hover:translate-x-0.5", resourcesMenuClasses.arrow)} />
                </>
              );

              if ((item as any).isExternal) {
                return (
                  <a 
                    key={item.name} 
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn("group flex items-center gap-3 rounded-xl px-2 py-2.5 transition-all duration-200", resourcesMenuClasses.itemHover)}
                    onClick={() => setActiveMenu(null)}
                  >
                    {content}
                  </a>
                );
              }

              return (
                <LocalizedLink 
                  key={item.name} 
                  to={item.href} 
                  className={cn("group flex items-center gap-3 rounded-xl px-2 py-2.5 transition-all duration-200", resourcesMenuClasses.itemHover)}
                  onClick={() => setActiveMenu(null)}
                >
                  {content}
                </LocalizedLink>
              );
            })}
          </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <header className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        isLightPage
          ? scrolled
            ? "bg-white/80 backdrop-blur-xl border-b border-black/[0.06]"
            : "bg-transparent"
          : scrolled 
            ? "bg-black/80 backdrop-blur-xl border-b border-white/[0.06]" 
            : "bg-transparent"
      )}>
      <nav className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <LocalizedLink to="/" className="flex items-center gap-2">
            <img src={logoFull} alt="XPAND Agent" width={140} height={28} className={cn("h-7 w-auto", isLightPage && "brightness-0")} />
          </LocalizedLink>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) => (
              <div 
                key={item.name} 
                className="relative group"
                onMouseEnter={() => item.hasDropdown && setActiveMenu(item.name)}
                onMouseLeave={() => setActiveMenu(null)}
              >
                <LocalizedLink
                  to={item.href}
                  className={cn(
                    "flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors",
                    isLightPage
                      ? location.pathname === item.href || activeMenu === item.name
                        ? "text-black"
                        : "text-black/50 hover:text-black"
                      : location.pathname === item.href || activeMenu === item.name
                        ? "text-white"
                        : "text-neutral-400 hover:text-white"
                  )}
                >
                  {item.name}
                  {item.hasDropdown && (
                    <XpandChevronDown className={cn(
                      "w-3.5 h-3.5 transition-transform duration-200",
                      activeMenu === item.name && "rotate-180"
                    )} />
                  )}
                </LocalizedLink>
                
                {item.hasDropdown && activeMenu === item.name && (
                  <>
                    <div className="absolute top-full left-0 w-full h-4" />
                    <div className="animate-fade-in pt-2">
                      {renderMegaMenu(item.menuType || "")}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Right Side - Auth State Dependent */}
          <div className="hidden lg:flex items-center gap-3">
            <LanguageSwitcher variant="compact" />
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08] hover:border-white/[0.12] transition-all duration-300 backdrop-blur-sm">
                    <Avatar className="w-7 h-7 ring-1 ring-white/[0.1]">
                      {sellerLogo && <AvatarImage src={sellerLogo} alt="Profile" />}
                      <AvatarFallback className="bg-primary/20 text-primary text-xs font-medium">
                        {getUserInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <XpandChevronDown className="w-3.5 h-3.5 text-white/50" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-60 bg-white border border-[hsl(0_0%_90%)] rounded-2xl shadow-[0_32px_64px_-16px_rgba(0,0,0,0.15)] p-2 animate-scale-in z-50"
                >
                  {/* User info header */}
                  <div className="px-3 py-3 mb-1">
                    <p className="text-sm font-medium text-[hsl(0_0%_10%)]">{user.email}</p>
                    <p className="text-xs text-[hsl(0_0%_50%)] capitalize">{role || t("common.buyer")}</p>
                  </div>
                  
                  {/* Main navigation */}
                  <DropdownMenuItem asChild className="rounded-xl px-3 py-2.5 focus:bg-primary/10 cursor-pointer transition-colors">
                    <LocalizedLink to="/dashboard" className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Package className="w-4 h-4 text-primary" />
                      </div>
                      <span className="font-medium text-[hsl(0_0%_10%)]">{t("common.myDashboard")}</span>
                    </LocalizedLink>
                  </DropdownMenuItem>
                  
                  <DropdownMenuItem asChild className="rounded-xl px-3 py-2.5 focus:bg-[hsl(0_0%_95%)] cursor-pointer transition-colors">
                    <LocalizedLink to="/account" className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[hsl(0_0%_95%)] flex items-center justify-center">
                        <Settings className="w-4 h-4 text-[hsl(0_0%_40%)]" />
                      </div>
                      <span className="font-light text-[hsl(0_0%_20%)]">{t("common.accountSettings")}</span>
                    </LocalizedLink>
                  </DropdownMenuItem>

                  {/* Role-based menu items */}
                  {isSeller && (
                    <DropdownMenuItem asChild className="rounded-xl px-3 py-2.5 focus:bg-[hsl(0_0%_95%)] cursor-pointer transition-colors">
                      <LocalizedLink to="/seller" className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-[hsl(0_0%_95%)] flex items-center justify-center">
                          <XpandStore className="w-4 h-4 text-[hsl(0_0%_40%)]" />
                        </div>
                        <span className="font-light text-[hsl(0_0%_20%)]">{t("nav.sellerDashboard")}</span>
                      </LocalizedLink>
                    </DropdownMenuItem>
                  )}

                  {(isAdmin || isSupport) && (
                    <>
                      <div className="my-2 mx-3">
                        <p className="text-[10px] font-semibold tracking-widest text-[hsl(0_0%_60%)] uppercase">{t("common.staff")}</p>
                      </div>
                      {isAdmin && (
                        <DropdownMenuItem asChild className="rounded-xl px-3 py-2.5 focus:bg-[hsl(0_0%_95%)] cursor-pointer transition-colors">
                          <LocalizedLink to="/admin" className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-[hsl(0_0%_95%)] flex items-center justify-center">
                              <Shield className="w-4 h-4 text-[hsl(0_0%_40%)]" />
                            </div>
                            <span className="font-light text-[hsl(0_0%_20%)]">{t("nav.adminPanel")}</span>
                          </LocalizedLink>
                        </DropdownMenuItem>
                      )}
                      {isSupport && (
                        <DropdownMenuItem asChild className="rounded-xl px-3 py-2.5 focus:bg-[hsl(0_0%_95%)] cursor-pointer transition-colors">
                          <LocalizedLink to="/support" className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-[hsl(0_0%_95%)] flex items-center justify-center">
                              <LifeBuoy className="w-4 h-4 text-[hsl(0_0%_40%)]" />
                            </div>
                            <span className="font-light text-[hsl(0_0%_20%)]">{t("nav.supportPanel")}</span>
                          </LocalizedLink>
                        </DropdownMenuItem>
                      )}
                    </>
                  )}

                  {!isSeller && (
                    <DropdownMenuItem asChild className="rounded-xl px-3 py-2.5 focus:bg-primary/10 cursor-pointer transition-colors">
                      <LocalizedLink to="/become-seller" className="flex items-center gap-3 text-primary/80">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <XpandTrending className="w-4 h-4 text-primary" />
                        </div>
                        <span className="font-light">{t("common.becomeSeller")}</span>
                      </LocalizedLink>
                    </DropdownMenuItem>
                  )}

                  <div className="my-2 border-t border-[hsl(0_0%_92%)]" />
                  
                  <DropdownMenuItem 
                    onClick={handleSignOut}
                    className="rounded-xl px-3 py-2.5 focus:bg-red-500/10 cursor-pointer transition-colors"
                  >
                    <div className="flex items-center gap-3 text-red-500">
                      <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                        <LogOut className="w-4 h-4 text-red-500" />
                      </div>
                      <span className="font-light">{t("nav.signOut")}</span>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <a 
                  href="https://portal.xpandagent.io/auth" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 text-sm font-medium bg-neutral-900 text-white rounded-full border border-neutral-800 hover:bg-neutral-800 transition-colors"
                >
                  {t("common.portalAccess")}
                </a>
                <LocalizedLink 
                  to="/auth" 
                  className="px-4 py-2 text-sm font-medium bg-white text-black rounded-full hover:bg-neutral-200 transition-colors"
                >
                  {t("common.signUp")}
                </LocalizedLink>
              </>
            )}
          </div>

          <div className="lg:hidden relative z-[60] flex items-center gap-1.5 sm:gap-2">
            <LanguageSwitcher
              variant="header"
              className={cn(isLightPage ? "text-black/70 hover:text-black" : "")}
            />

            <button
              type="button"
              className={cn(
                "min-w-[44px] min-h-[44px] rounded-full flex items-center justify-center active:scale-95 transition-all duration-150 border border-transparent bg-transparent shadow-none backdrop-blur-0",
                isLightPage
                  ? "text-black/70 hover:text-primary"
                  : "text-white/72 hover:text-white"
              )}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setMobileMenuOpen(!mobileMenuOpen);
              }}
              aria-label={mobileMenuOpen ? t("nav.closeMenu") : t("nav.openMenu")}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <XpandX className="w-5 h-5" /> : <XpandMenu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </nav>
      </header>

      {/* Mobile Menu - Full Screen Overlay - Outside header for proper z-index */}
      {mobileMenuOpen && (
        <div className={cn(
          "lg:hidden fixed inset-x-0 bottom-0 z-[100] animate-fade-in",
          "top-16",
          "bg-[#050a13] border-t border-white/[0.06]"
        )}>
          {/* Subtle purple glow from bottom */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[400px] h-[300px] bg-primary/15 rounded-full blur-[120px]" />
            <div className="absolute bottom-0 right-0 w-[200px] h-[200px] bg-primary/10 rounded-full blur-[80px]" />
          </div>
          <div className="relative z-10 px-4 py-6 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {/* Home link */}
            <div className="border-b border-white/[0.06]">
              <LocalizedLink
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center w-full py-4 text-base font-medium text-white/80 hover:text-white transition-colors"
              >
                {t('nav.home', 'Home')}
              </LocalizedLink>
            </div>

            {navigation.map((item) => (
              <div key={item.name} className="border-b border-white/[0.06]">
                {item.hasDropdown ? (
                  <>
                    {/* Expandable menu item */}
                    <button
                      onClick={() => toggleMobileSubmenu(item.name)}
                      className="flex items-center justify-between w-full py-4 text-base font-medium text-white/80 hover:text-white transition-colors"
                    >
                      {item.name}
                      <XpandChevronDown 
                        className={cn(
                          "w-5 h-5 text-white/40 transition-transform duration-300",
                          expandedMobileMenu === item.name && "rotate-180"
                        )} 
                      />
                    </button>
                    
                    {/* Submenu */}
                    {expandedMobileMenu === item.name && (
                      <div className="pb-4 pl-2 space-y-4 animate-fade-in">
                        {getMobileMenuItems(item.menuType || "").map((category: any) => (
                          <div key={category.category}>
                            <p className="text-[10px] font-semibold tracking-widest text-primary/70 uppercase mb-3 px-2">
                              {category.category}
                            </p>
                            <div className="space-y-0.5">
                              {category.items.map((subItem: any) => {
                                const linkContent = (
                                  <>
                                    <div className="w-9 h-9 rounded-lg bg-white/[0.04] flex items-center justify-center shrink-0 group-hover:bg-primary/10 transition-colors overflow-hidden">
                                      {subItem.logo ? (
                                        <img src={subItem.logo} alt={subItem.name} className="w-full h-full object-cover" />
                                      ) : subItem.icon ? (
                                        <subItem.icon className="w-4 h-4 text-primary/70" strokeWidth={1.5} />
                                      ) : (
                                        <XpandZap className="w-4 h-4 text-primary/70" strokeWidth={1.5} />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-white/90 group-hover:text-white transition-colors">
                                        {subItem.name}
                                      </p>
                                      <p className="text-xs text-white/40 truncate">
                                        {subItem.description}
                                      </p>
                                    </div>
                                    <XpandArrowRight className="w-4 h-4 text-white/20 group-hover:text-primary/60 shrink-0" />
                                  </>
                                );

                                if (subItem.isExternal) {
                                  return (
                                    <a
                                      key={subItem.name}
                                      href={subItem.href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.04] transition-colors group"
                                      onClick={() => setMobileMenuOpen(false)}
                                    >
                                      {linkContent}
                                    </a>
                                  );
                                }

                                return (
                                  <LocalizedLink
                                    key={subItem.name}
                                    to={subItem.href}
                                    className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.04] transition-colors group"
                                    onClick={() => setMobileMenuOpen(false)}
                                  >
                                    {linkContent}
                                  </LocalizedLink>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                        
                        {/* Special CTAs for AI Store */}
                        {item.menuType === "store" && (
                          <LocalizedLink
                            to="/store"
                            className="flex items-center gap-3 mx-2 p-3 rounded-xl bg-primary/10 border border-primary/20 hover:bg-primary/15 transition-colors"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            <div className="w-9 h-9 rounded-lg bg-primary/20 flex items-center justify-center">
                              <XpandStore className="w-4 h-4 text-primary" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-white">{t("nav.browseAllAgents")}</p>
                              <p className="text-xs text-white/50">{t("nav.agentsAvailable")}</p>
                            </div>
                            <XpandArrowRight className="w-4 h-4 text-primary" />
                          </LocalizedLink>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <LocalizedLink
                    to={item.href}
                    className="flex items-center justify-between py-4 text-base font-medium text-white/80 hover:text-white transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </LocalizedLink>
                )}
              </div>
            ))}
            
            {/* User Section */}
            {user ? (
              <div className="pt-6 space-y-1">
                <p className="text-[10px] font-semibold tracking-widest text-white/30 uppercase mb-3 px-2">
                  {t("common.yourAccount")}
                </p>
                <LocalizedLink
                  to="/dashboard"
                  className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.04] transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Package className="w-5 h-5 text-white/50" />
                  <span className="text-sm font-medium text-white/80">{t("common.myDashboard")}</span>
                </LocalizedLink>
                <LocalizedLink
                  to="/account"
                  className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.04] transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Settings className="w-5 h-5 text-white/50" />
                  <span className="text-sm font-medium text-white/80">{t("common.accountSettings")}</span>
                </LocalizedLink>
                {isSeller && (
                  <LocalizedLink
                    to="/seller"
                    className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.04] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <XpandStore className="w-5 h-5 text-white/50" />
                    <span className="text-sm font-medium text-white/80">{t("nav.sellerDashboard")}</span>
                  </LocalizedLink>
                )}
                {isAdmin && (
                  <LocalizedLink
                    to="/admin"
                    className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.04] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Shield className="w-5 h-5 text-white/50" />
                    <span className="text-sm font-medium text-white/80">{t("nav.adminPanel")}</span>
                  </LocalizedLink>
                )}
                {isSupport && (
                  <LocalizedLink
                    to="/support"
                    className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.04] transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <LifeBuoy className="w-5 h-5 text-white/50" />
                    <span className="text-sm font-medium text-white/80">{t("nav.supportPanel")}</span>
                  </LocalizedLink>
                )}
                {!isSeller && (
                  <LocalizedLink
                    to="/become-seller"
                    className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-white/[0.04] transition-colors text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <XpandTrending className="w-5 h-5" />
                    <span className="text-sm font-medium">{t("common.becomeSeller")}</span>
                  </LocalizedLink>
                )}
                <button
                  onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-red-500/10 transition-colors w-full text-red-400"
                >
                  <LogOut className="w-5 h-5" />
                  <span className="text-sm font-medium">{t("nav.signOut")}</span>
                </button>
              </div>
            ) : (
              <div className="pt-6 space-y-3 px-2">
                <a
                  href="https://portal.xpandagent.io/auth"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center px-5 py-3.5 text-sm font-medium bg-white/[0.04] text-white rounded-xl border border-white/[0.08] hover:bg-white/[0.08] transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("common.portalAccess")}
                </a>
                <LocalizedLink
                  to="/auth"
                  className="block w-full text-center px-5 py-3.5 text-sm font-medium bg-white text-black rounded-xl hover:bg-white/90 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t("common.signUp")}
                </LocalizedLink>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}